import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import {
    MAT_DIALOG_DATA,
    MatDialogModule,
    MatDialogRef,
} from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { Router } from '@angular/router';
import { TranslatePipe } from 'app/core/pipe/translate.pipe';
import { UserService } from 'app/shared/services/user.service';

export interface ProfileCompletionGateData {
    featureName?: string;
    /** Whether the profile is in Error state (admin requested corrections) */
    isErrorState?: boolean;
    /** Admin's rejection reason message */
    errorMessage?: string;
    /** Fields flagged for correction */
    errorFields?: string[];
}

/**
 * Dialog shown when a filmmaker tries to access a feature before
 * their profile has been approved.
 *
 * Two variants:
 * - Incomplete: "Complete Your Profile" → navigates to /onboarding
 * - Error: "Corrections Required" → navigates to /film-maker/error-resolution
 */
@Component({
    selector: 'app-profile-completion-gate',
    standalone: true,
    imports: [
        CommonModule,
        MatDialogModule,
        MatButtonModule,
        MatIconModule,
        TranslatePipe,
    ],
    templateUrl: './profile-completion-gate.component.html',
})
export class ProfileCompletionGateComponent {
    private readonly _dialogRef = inject(
        MatDialogRef<ProfileCompletionGateComponent>
    );
    private readonly _router = inject(Router);
    private readonly _userService = inject(UserService);

    readonly data: ProfileCompletionGateData = inject(MAT_DIALOG_DATA) ?? {};

    get isError(): boolean {
        return !!this.data.isErrorState;
    }

    get errorFieldLabels(): string[] {
        return (this.data.errorFields || []).map((key) =>
            this._camelCaseToLabel(key)
        );
    }

    cancel(): void {
        this._dialogRef.close(false);
    }

    continue(): void {
        this._dialogRef.close(true);
        if (this.isError) {
            this._router.navigate(['/film-maker/error-resolution']);
        } else {
            // Invalidate the profile cache so OnboardingMasterComponent.ngOnInit()
            // always fetches fresh data from the backend instead of returning the
            // 5-minute cached version. Without this, the onboarding page shows
            // empty forms until the user manually refreshes.
            this._userService.clearCachedProfile();
            this._router.navigate(['/onboarding']);
        }
    }

    private _camelCaseToLabel(key: string): string {
        if (key.includes(' ')) return key;
        const words = key
            .replace(/([a-z\d])([A-Z])/g, '$1 $2')
            .replace(/([A-Z])([A-Z][a-z])/g, '$1 $2')
            .split(' ');
        return words
            .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
            .join(' ');
    }
}
